var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { arrow, autoUpdate, flip, limitShift, offset as floatingOffset, shift, size as floatingSize, useDismiss, useFloating, useInteractions, useRole, } from '@floating-ui/react';
import { cloneElement, useRef } from 'react';
import { useId, useMergeRefs } from '../hooks';
import { Modal } from '../modal';
import { tokens } from '../tokens';
import Content from './content';
import { wrapperStyle } from './styles';
const FLOATING_PADDING = parseInt(tokens.spacingNew.medium, 10);
// Mapping between Waffles and floating-ui placement
const placementMap = {
    bottom: 'bottom',
    bottomLeft: 'bottom-start',
    bottomRight: 'bottom-end',
    left: 'left',
    right: 'right',
    top: 'top',
    topLeft: 'top-start',
    topRight: 'top-end',
};
function Popover(_a) {
    var { children, closeButtonOverride, closeOnOutsidePress = false, disableArrow = false, highlightVariant = 'green', isOpen, isPortalled = false, offset = tokens.spacingNew.medium, onClose, placement = 'bottomLeft', size = 'medium', target, trapFocus = false, variant = 'regular' } = _a, restProps = __rest(_a, ["children", "closeButtonOverride", "closeOnOutsidePress", "disableArrow", "highlightVariant", "isOpen", "isPortalled", "offset", "onClose", "placement", "size", "target", "trapFocus", "variant"]);
    const arrowRef = useRef(null);
    const popoverId = `popover-${useId()}`;
    const parsedOffset = parseInt(offset, 10);
    const { context, placement: calculatedPlacement, refs, } = useFloating({
        middleware: [
            floatingOffset(parsedOffset),
            floatingSize({
                apply({ availableHeight, availableWidth, elements }) {
                    Object.assign(elements.floating.style, {
                        // Flip middleware will take over beyond when available space is below maxes
                        maxHeight: Math.max(600, availableHeight),
                        maxWidth: Math.max(300, availableWidth),
                    });
                },
                padding: FLOATING_PADDING,
            }),
            shift({
                crossAxis: false,
                limiter: limitShift({ offset: parsedOffset * 2 }),
                padding: FLOATING_PADDING,
            }),
            flip({
                fallbackAxisSideDirection: 'end',
                fallbackStrategy: 'bestFit',
                padding: FLOATING_PADDING,
            }),
            arrow({
                element: arrowRef,
                padding: parseInt(tokens.spacingNew.xsmall, 10),
            }),
        ],
        onOpenChange: onClose,
        open: isOpen,
        placement: placementMap[placement],
        whileElementsMounted: autoUpdate,
    });
    const { getFloatingProps, getReferenceProps } = useInteractions([
        useRole(context),
        useDismiss(context, {
            outsidePress: closeOnOutsidePress,
        }),
    ]);
    // @ts-expect-error: target.ref not recognized
    const targetRef = useMergeRefs(refs.setReference, target.ref);
    const targetEl = isOpen
        ? cloneElement(target, getReferenceProps(Object.assign(Object.assign({ key: target.key, ref: targetRef }, target.props), { 'aria-controls': undefined })))
        : target;
    return (_jsxs(_Fragment, { children: [targetEl, _jsx(Modal, Object.assign({ css: wrapperStyle({ isOpen, placement }) }, Object.assign({ closeButtonOverride,
                context, disableOverlay: true, disablePortal: !isPortalled, floatingProps: getFloatingProps({
                    id: popoverId,
                    ref: refs.setFloating,
                }), isNonModal: !trapFocus, isOpen,
                onClose,
                size }, restProps), { children: _jsx(Content, Object.assign({}, Object.assign({ context, hasRightDecor: calculatedPlacement === 'right', highlightVariant,
                    size,
                    variant }, (!disableArrow && { arrowRef })), { children: children })) }))] }));
}
export default Popover;
